
import { CANVAS_WIDTH, CANVAS_HEIGHT } from "../config/dimensions";
import { COLORS } from "../config/colors";

export const drawNamsanBackground = (ctx: CanvasRenderingContext2D) => {
    // Red Sky (Apocalypse)
    const sky = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    sky.addColorStop(0, "#450a0a"); // Dark Red
    sky.addColorStop(1, "#1c1917"); // Dark Stone
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Tower Silhouette
    ctx.save();
    ctx.translate(CANVAS_WIDTH / 2, 200);
    ctx.scale(0.8, 0.8);
    ctx.fillStyle = "#000";
    ctx.beginPath();
    ctx.moveTo(-20, 0); ctx.lineTo(20, 0); ctx.lineTo(5, -150); ctx.lineTo(-5, -150); ctx.fill(); // Stem
    ctx.beginPath(); ctx.arc(0, -150, 30, 0, Math.PI*2); ctx.fill(); // Top pod
    ctx.beginPath(); ctx.moveTo(-2, -180); ctx.lineTo(2, -180); ctx.lineTo(0, -250); ctx.fill(); // Antenna
    // Glowing red windows
    ctx.fillStyle = "#ef4444";
    ctx.shadowColor = "#ef4444"; ctx.shadowBlur = 20;
    ctx.fillRect(-10, -155, 20, 2);
    ctx.restore();

    // Floating Debris
    const time = Date.now() / 1000;
    ctx.fillStyle = "#000";
    for(let i=0; i<10; i++) {
        const x = (i * 100 + time * 20) % CANVAS_WIDTH;
        const y = CANVAS_HEIGHT - 100 - Math.sin(time + i) * 50;
        ctx.fillRect(x, y, 20, 20);
    }
};

export const drawBathhouseBackground = (ctx: CanvasRenderingContext2D) => {
    // 1. Base Dark Green Background (Toxic Fog Vibe)
    const bgGrad = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    bgGrad.addColorStop(0, "#022c22"); // Very dark green (teal-950)
    bgGrad.addColorStop(0.5, "#064e3b"); // Green-900
    bgGrad.addColorStop(1, "#022c22");
    ctx.fillStyle = bgGrad;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // 2. Volumetric Green Glow (Center Atmosphere)
    const glow = ctx.createRadialGradient(CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, 50, CANVAS_WIDTH / 2, CANVAS_HEIGHT / 2, CANVAS_WIDTH);
    glow.addColorStop(0, "rgba(52, 211, 153, 0.15)"); // Emerald-400 tint
    glow.addColorStop(1, "rgba(0,0,0,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // 3. The Large Circular Pods (Bio-tanks)
    const drawPod = (x: number, y: number, radius: number) => {
        ctx.save();
        ctx.translate(x, y);

        // Outer Structural Rim
        ctx.beginPath();
        ctx.arc(0, 0, radius + 15, 0, Math.PI * 2);
        ctx.fillStyle = "#022c22"; 
        ctx.fill();
        ctx.strokeStyle = "#065f46"; // Dark Emerald border
        ctx.lineWidth = 5;
        ctx.stroke();

        // Inner Glow (The liquid/glass interface)
        const podGrad = ctx.createRadialGradient(0, 0, radius * 0.4, 0, 0, radius);
        podGrad.addColorStop(0, "#2dd4bf"); // Teal-400 (Bright center liquid)
        podGrad.addColorStop(0.7, "#0f766e"); // Teal-700
        podGrad.addColorStop(1, "#134e4a"); // Teal-900 (Edge)
        
        ctx.beginPath();
        ctx.arc(0, 0, radius, 0, Math.PI * 2);
        ctx.fillStyle = podGrad;
        
        // Add neon glow
        ctx.shadowColor = "#2dd4bf";
        ctx.shadowBlur = 30;
        ctx.fill();
        ctx.shadowBlur = 0;

        // Silhouette inside pod (Spidery Shadow)
        ctx.save();
        ctx.clip(); // Clip drawing to inside the pod
        ctx.fillStyle = "rgba(0, 20, 10, 0.5)";
        ctx.beginPath();
        // Abstract spindly shape representing dormant demon
        ctx.moveTo(0, 10);
        ctx.quadraticCurveTo(-30, -30, -40, -10);
        ctx.quadraticCurveTo(-20, 0, -30, 40);
        ctx.moveTo(0, 10);
        ctx.quadraticCurveTo(30, -30, 40, -10);
        ctx.quadraticCurveTo(20, 0, 30, 40);
        ctx.fill();
        ctx.restore();

        // Glass Highlight
        ctx.fillStyle = "rgba(255, 255, 255, 0.1)";
        ctx.beginPath();
        ctx.ellipse(-radius * 0.3, -radius * 0.3, radius * 0.2, radius * 0.1, -0.5, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
    };

    // Draw two large pods in background
    const podY = CANVAS_HEIGHT / 2 - 60;
    const podRadius = 140;
    
    // Draw connecting pipes/structure behind pods
    ctx.strokeStyle = "#064e3b";
    ctx.lineWidth = 20;
    ctx.beginPath();
    ctx.moveTo(CANVAS_WIDTH * 0.3, podY);
    ctx.lineTo(CANVAS_WIDTH * 0.7, podY);
    ctx.stroke();

    drawPod(CANVAS_WIDTH * 0.25, podY, podRadius);
    drawPod(CANVAS_WIDTH * 0.75, podY, podRadius);

    // 4. Floor / Liquid Surface
    const floorY = CANVAS_HEIGHT - 120;
    
    // Reflection of the scene on the wet floor
    ctx.save();
    ctx.translate(0, floorY);
    ctx.scale(1, -0.4); // Squashed reflection
    ctx.translate(0, -floorY);
    
    // Reflect Pods
    ctx.globalAlpha = 0.2;
    ctx.fillStyle = "#2dd4bf";
    ctx.beginPath(); ctx.arc(CANVAS_WIDTH * 0.25, podY, podRadius, 0, Math.PI*2); ctx.fill();
    ctx.beginPath(); ctx.arc(CANVAS_WIDTH * 0.75, podY, podRadius, 0, Math.PI*2); ctx.fill();
    
    ctx.restore();

    // Floor Gradient
    const waterGrad = ctx.createLinearGradient(0, floorY, 0, CANVAS_HEIGHT);
    waterGrad.addColorStop(0, "rgba(6, 78, 59, 0.4)"); // Emerald-900 transparent
    waterGrad.addColorStop(1, "#022c22"); // Dark bottom
    
    ctx.fillStyle = waterGrad;
    ctx.fillRect(0, floorY, CANVAS_WIDTH, CANVAS_HEIGHT - floorY);

    // Pool edge
    ctx.fillStyle = "#064e3b";
    ctx.fillRect(0, floorY, CANVAS_WIDTH, 10);

    // 5. Fog / Steam Overlay (Volumetric Lighting)
    const time = Date.now() / 3000;
    ctx.fillStyle = "rgba(167, 243, 208, 0.08)"; // Very light green mist
    for(let i=0; i<6; i++) {
        const x = (i * 300 + Math.sin(time + i)*100) % (CANVAS_WIDTH + 200) - 100;
        const y = CANVAS_HEIGHT - (time * 50 + i * 100) % CANVAS_HEIGHT;
        const size = 150 + Math.sin(i)*50;
        ctx.beginPath(); 
        ctx.arc(x, y, size, 0, Math.PI*2); 
        ctx.fill();
    }
    
    // Digital Scanlines / Noise (Sci-fi feel)
    ctx.fillStyle = "rgba(52, 211, 153, 0.03)";
    for(let y=0; y<CANVAS_HEIGHT; y+=6) {
        ctx.fillRect(0, y, CANVAS_WIDTH, 2);
    }
};

export const drawCastleBackground = (ctx: CanvasRenderingContext2D) => {
    // Night Sky
    const sky = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    sky.addColorStop(0, "#0f172a"); // Slate-900
    sky.addColorStop(1, "#1e1b4b"); // Indigo-950
    ctx.fillStyle = sky;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Moon
    ctx.fillStyle = "#fef3c7";
    ctx.shadowColor = "#fef3c7"; ctx.shadowBlur = 30;
    ctx.beginPath(); ctx.arc(100, 100, 40, 0, Math.PI*2); ctx.fill();
    ctx.shadowBlur = 0;

    // Stone Bricks pattern
    ctx.fillStyle = "rgba(0,0,0,0.3)";
    for(let y=CANVAS_HEIGHT-200; y<CANVAS_HEIGHT; y+=30) {
        for(let x=0; x<CANVAS_WIDTH; x+=60) {
            ctx.fillRect(x + (y%60)/2, y, 55, 25);
        }
    }
};

export const drawDefaultStage = (ctx: CanvasRenderingContext2D) => {
    const g = ctx.createLinearGradient(0,0,0,CANVAS_HEIGHT);
    g.addColorStop(0, COLORS.bg); 
    g.addColorStop(1, COLORS.stage);
    ctx.fillStyle = g; 
    ctx.fillRect(0,0,CANVAS_WIDTH,CANVAS_HEIGHT);

    // Spotlights
    ctx.globalAlpha = 0.3; 
    ctx.fillStyle="#fff";
    for(let i=0; i<5; i++) {
      ctx.beginPath(); 
      ctx.moveTo(100+i*150,0); 
      ctx.lineTo(50+i*150,CANVAS_HEIGHT); 
      ctx.lineTo(150+i*150,CANVAS_HEIGHT); 
      ctx.fill();
    }
    ctx.globalAlpha = 1.0;
};

export const drawPlaneBackground = (ctx: CanvasRenderingContext2D) => {
    // Dark interior gradient
    const bg = ctx.createLinearGradient(0, 0, 0, CANVAS_HEIGHT);
    bg.addColorStop(0, "#1e293b"); // slate-800
    bg.addColorStop(1, "#0f172a"); // slate-900
    ctx.fillStyle = bg;
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);

    // Floor
    ctx.fillStyle = "#334155"; // slate-700
    ctx.fillRect(0, CANVAS_HEIGHT - 60, CANVAS_WIDTH, 60);
    
    // Carpet/floor details
    ctx.fillStyle = "rgba(236, 72, 153, 0.2)"; // pink glow
    ctx.fillRect(0, CANVAS_HEIGHT - 55, CANVAS_WIDTH, 5);
    ctx.fillRect(0, CANVAS_HEIGHT - 10, CANVAS_WIDTH, 5);
    
    // Curved Ceiling/Walls
    ctx.fillStyle = "#1e293b";
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(CANVAS_WIDTH, 0);
    ctx.lineTo(CANVAS_WIDTH, CANVAS_HEIGHT - 250);
    ctx.quadraticCurveTo(CANVAS_WIDTH/2, CANVAS_HEIGHT - 280, 0, CANVAS_HEIGHT - 250);
    ctx.closePath();
    ctx.fill();

    // Windows
    for (let i = 0; i < 3; i++) {
        const x = CANVAS_WIDTH / 2 - 250 + i * 250;
        const y = 150;
        // Window frame
        ctx.fillStyle = "#0f172a";
        ctx.beginPath();
        ctx.roundRect(x - 55, y - 35, 110, 70, 20);
        ctx.fill();

        // Window glass (dark sky)
        ctx.fillStyle = "#020617";
        ctx.beginPath();
        ctx.roundRect(x-50, y-30, 100, 60, 15);
        ctx.fill();
        
        // Stars
        ctx.fillStyle = "white";
        for(let j=0; j<3; j++) {
            const starX = x - 40 + Math.random() * 80;
            const starY = y - 25 + Math.random() * 50;
            ctx.fillRect(starX, starY, Math.random() > 0.5 ? 1 : 2, 1);
        }
    }
    
    // Flickering overhead lights
    const flicker = Math.random() > 0.1 ? 1.0 : 0.5;
    ctx.globalAlpha = flicker;
    ctx.fillStyle = "rgba(34, 211, 238, 0.4)"; // cyan
    ctx.shadowColor = "#22d3ee";
    ctx.shadowBlur = 20;
    ctx.fillRect(0, 5, CANVAS_WIDTH, 10);
    ctx.shadowBlur = 0;
    ctx.globalAlpha = 1.0;
};

export const drawPracticeBackground = (ctx: CanvasRenderingContext2D) => {
    // Generic Gray Room
    ctx.fillStyle = "#334155"; // Slate-700 floor/wall
    ctx.fillRect(0, 0, CANVAS_WIDTH, CANVAS_HEIGHT);
    
    // Grid Lines
    ctx.strokeStyle = "rgba(255,255,255,0.1)";
    ctx.lineWidth = 2;
    // Perspective Grid floor
    const horizon = CANVAS_HEIGHT * 0.6;
    
    // Wall
    ctx.fillStyle = "#1e293b"; // Darker wall
    ctx.fillRect(0, 0, CANVAS_WIDTH, horizon);
    
    // Floor
    ctx.fillStyle = "#334155";
    ctx.fillRect(0, horizon, CANVAS_WIDTH, CANVAS_HEIGHT - horizon);

    // Floor Grid
    ctx.beginPath();
    for (let i = 0; i < 20; i++) {
        const x = i * (CANVAS_WIDTH / 10);
        ctx.moveTo(x, horizon);
        ctx.lineTo((x - CANVAS_WIDTH/2) * 4 + CANVAS_WIDTH/2, CANVAS_HEIGHT);
    }
    // Horizontal floor lines
    for (let i = 0; i < 10; i++) {
        const y = horizon + Math.pow(i, 2) * 4;
        if (y > CANVAS_HEIGHT) break;
        ctx.moveTo(0, y);
        ctx.lineTo(CANVAS_WIDTH, y);
    }
    ctx.stroke();
    
    // "PRACTICE MODE" Text on wall
    ctx.save();
    ctx.fillStyle = "rgba(255,255,255,0.05)";
    ctx.font = "bold 80px sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("PRACTICE", CANVAS_WIDTH/2, horizon - 50);
    ctx.restore();
};
